/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";
import { assetImages } from "constants";
import PatientDetailsCard from "../PatientDetailsCard/PatientDetailsCard";

const SaveHeightPopup = ({
  patientObj = null,
  afterActionClose = () => {},
  selectedHeightId,
  setSelectedHeightId,
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  // States for input fields
  const [height, setHeight] = useState("");
  const [heightDate, setHeightDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // Default to today's date
  const [assignTo, setAssignTo] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] =
    useState(false); /* New state for loading height details */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!height) {
      newErrors.height = t("Please enter height value!");
    } else if (isNaN(height) || height <= 0) {
      newErrors.height = t("Please enter a valid height number!");
    }
    if (!heightDate) {
      // Validation for new date field
      newErrors.heightDate = t("Please enter a date!");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Async load options for Assign To (from AddAllergyModal)
  const loadAssineeOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=PATIENT`;

      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  //function for get height details
  const getHeightDetails = async () => {
    try {
      setIsLoading(true); /* Set loading to true before API call */
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_HEIGHT_BY_ID +
        `/${selectedHeightId}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const heightData = response.data;

        setHeight(heightData.height);
        setHeightDate(
          heightData.date
            ? new Date(heightData.date).toISOString().split("T")[0]
            : ""
        ); // Set date from API data
        setAssignTo(heightData?.uservalue);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(
        false
      ); /* Set loading to false after API call (success or failure) */
    }
  };

  const saveHeightHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    try {
      setIsSaving(true);
      const apiData = {
        height: parseFloat(height),
        date: heightDate, // Include date in API data
        user: assignTo ? assignTo?.value : null,
      };

      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (selectedHeightId) {
        requestUrl +=
          url.API_UPDATE_HEIGHT + `/${selectedHeightId}?token=${token}`;
        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_CREATE_HEIGHT + `?token=${token}`;
        response = await postData(requestUrl, apiData);
      }

      console.log("response in save height", response);

      setIsSaving(false);

      if (response.status) {
        resetHandler();
        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#offcanvasSaveHeight")
        ).hide();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.error("Error saving height:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHandler = () => {
    setHeight("");
    setHeightDate(new Date().toISOString().split("T")[0]); // Reset to today's date
    setAssignTo(patientObj);
    setErrors({});
    setSelectedHeightId(null);
  };

  useEffect(() => {
    if (patientObj && !selectedHeightId) {
      setAssignTo(patientObj);
    }
  }, [patientObj, selectedHeightId]);

  useEffect(() => {
    if (selectedHeightId) {
      getHeightDetails();
    }
  }, [selectedHeightId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0"
      tabIndex="-1"
      id="offcanvasSaveHeight"
      aria-labelledby="offcanvasSaveHeightLabel"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              height
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasSaveHeightLabel"
            >
              Save Height
            </h3>
            <p className="text-white fs-sm mb-0 mt-2">
              Enter and save height measurements
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4" ref={offcanvasBodyRef}>
        {isLoading ? (
          <div className="loading-state text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">{t("Loading height responses...")}</p>
          </div>
        ) : null}

        {!isLoading && (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            <form
              className="d-flex flex-column h-100"
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="fields-container flex-fill">
                {/* Height input */}
                <div className="row">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="height"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Height (cm) *
                      </label>
                      <input
                        type="number"
                        id="height"
                        className={`form-control fs-sm shadow-none ${
                          errors.height
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="e.g., 170"
                        value={height}
                        onChange={(e) => {
                          setHeight(e.target.value);
                          if (errors.height) {
                            setErrors((prev) => ({ ...prev, height: "" }));
                          }
                        }}
                      />
                      {errors.height && (
                        <div className="invalid-feedback">{errors.height}</div>
                      )}
                    </div>
                  </div>
                  {/* Date input */}
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="heightDate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Date *
                      </label>
                      <input
                        type="date"
                        id="heightDate"
                        className={`form-control fs-sm shadow-none ${
                          errors.heightDate
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        value={heightDate}
                        onChange={(e) => {
                          setHeightDate(e.target.value);
                          if (errors.heightDate) {
                            setErrors((prev) => ({
                              ...prev,
                              heightDate: "",
                            }));
                          }
                        }}
                      />
                      {errors.heightDate && (
                        <div className="invalid-feedback">
                          {errors.heightDate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assign To field */}
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Assign To
                  </label>
                  <AsyncSelect
                    isClearable
                    name="assignTo"
                    loadOptions={loadAssineeOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    placeholder={t("Select Assignee")}
                    styles={getCustomStyles()}
                    value={assignTo}
                    onChange={setAssignTo}
                  />
                  {assignTo ? <PatientDetailsCard patient={assignTo} /> : null}
                </div>
              </div>

              {/* Height Interpretation Info */}
              <div className="p-4 border rounded-10 shadow-sm bg-gradient-light position-relative overflow-hidden">
                <div className="row align-items-center mb-3">
                  <div className="col">
                    <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <span className="material-symbols-outlined text-primary">
                        assessment
                      </span>
                      Height Interpretation
                    </h5>
                    <p className="text-muted fs-sm mb-0">
                      Reference ranges for adults
                    </p>
                  </div>
                </div>

                <div className="interpretation-grid">
                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Normal Range
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Adult Height:</strong> Varies by gender and
                        ethnicity
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-top border-gray-200">
                  <p className="text-muted fs-xs mb-0 d-flex align-items-center gap-1">
                    <span className="material-symbols-outlined fs-6">info</span>
                    Consult your healthcare provider for proper interpretation
                    and treatment recommendations.
                  </p>
                </div>
              </div>

              <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={saveHeightHandler}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                  disabled={isSaving ? true : false}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        save
                      </span>{" "}
                      {t("Save Height")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveHeightPopup;
